import { useCallback, useEffect, useRef } from "react";

/**
 * A generic hook for handling hover selection with a configurable delay.
 * Useful for navigation menus where you want to delay selection until
 * the user has hovered for a certain amount of time.
 *
 * @param selectValue - Callback to invoke when selection should occur
 * @param delay - Delay in milliseconds before selection triggers
 * @returns Object with onMouseEnter and onMouseLeave handlers
 */
export const useHoverSelection = <T>(selectValue: (node: T, noChildren?: boolean) => void, delay: number) => {
  const timeoutRef = useRef<number>();

  const onMouseEnter = useCallback(
    (node: T, noChildren = false) => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
      timeoutRef.current = window.setTimeout(() => {
        selectValue(node, noChildren);
      }, delay);
    },
    [selectValue, delay],
  );

  const onMouseLeave = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
    }
  }, []);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) {
        clearTimeout(timeoutRef.current);
      }
    };
  }, []);

  return {
    onMouseEnter,
    onMouseLeave,
  };
};
